import {ScreenNames} from '../../../lib/analytics/events';
import {BaseScreen, ScreenDivider, ScreenHeader} from '../Screen';
import {UserLoader} from '../../../components/UserLoader';
import React from 'react';
import {UserProfileInterface} from '../../../reducers/users';
import {ReactComponent as KeyIcon} from 'src/assets/icons/key-01.svg';
import {DataLoader} from '../../../components/DataLoader';
import {nFetch} from '../../../api/nFetch';
import {ApiKey} from '../../../api/types/api_keys';
import {Button} from '../../../ui-components/Button/button';
import {Input} from '../profile/Profile';
import {CopyIcon, DeleteIcon, DeleteRed} from '../../../ui-components/Iconpack';
import {toast, ToastContainer} from 'react-toastify';

class ApiKeyCrud {
    static async create(org_uid: string, name: string) {
        return await nFetch<ApiKey>(`/organization/${org_uid}/api_keys/`, {
            method: 'POST',
            body: JSON.stringify({name}),
        });
    }

    static async delete(org_uid: string, prefix: string) {
        return await nFetch(
            `/organization/${org_uid}/api_keys/${prefix}/`,
            {
                method: 'DELETE',
            },
            false,
        );
    }

    static async list(org_uid: string) {
        return await nFetch<ApiKey[]>(`/organization/${org_uid}/api_keys/`);
    }
}

function ApiKeyMgmt(props: {org_uid: string; api_keys: ApiKey[]}) {
    let [keys, setKeys] = React.useState<ApiKey[]>(props.api_keys);
    let [newName, setNewName] = React.useState<string>('');

    let onCreate = async () => {
        const result = await ApiKeyCrud.create(org_uid, newName);
        if (!!result.error) {
            toast(result.error.message ?? 'Failed to create key', {
                type: 'error',
            });
            return;
        }
        setKeys([result.data!, ...keys]);
        setNewName('');
    };

    let onDelete = async (k: ApiKey) => {
        const confirm = window.confirm('Are you sure you want to delete this key? Any integrations will stop working.');
        if (!confirm) return;
        const confirm2 = window.confirm('Are you really sure? This action cannot be undone.');
        if (!confirm2) return;

        const result = await ApiKeyCrud.delete(org_uid, k.prefix);
        if (!!result.error) {
            toast(result.error.message ?? 'Failed to delete key', {
                type: 'error',
            });
            return;
        }
        toast('Deleted', {type: 'success'});
        setKeys(keys.filter((key) => key.prefix !== k.prefix));
    };

    const {org_uid} = props;
    return (
        <div>
            <div className='m-4 flex flex-col gap-4'>
                <span className='text-[14px] font-[600] text-[#344054]'>Create new key</span>
                <Input
                    type={'text'}
                    placeholder={'Enter a key name'}
                    title={'Name'}
                    value={newName}
                    onChange={(e) => {
                        setNewName(e.target.value);
                    }}
                />
                <Button className='w-full' onClick={onCreate}>
                    Create
                </Button>
            </div>
            <div className='mt-4 border border-[#EAECF0]'></div>
            <div className='px-3 py-4'>
                <table className={'w-full text-[12px] font-[400] md:text-[1rem]'}>
                    <thead>
                        <tr>
                            <th className={'text-left text-[14px] font-[700] text-[#344054]'}>Name</th>
                            <th className={'text-left text-[14px] font-[700] text-[#344054]'}>Created</th>
                            <th className={'text-left text-[14px] font-[700] text-[#344054]'}>Key</th>
                        </tr>
                    </thead>
                    <tbody>
                        {keys.map((k) => {
                            return (
                                <>
                                    <tr key={k.prefix}>
                                        <td className={''}>
                                            {!!k.name ? k.name : <span className={'italic'}>&lt;blank&gt;</span>}
                                        </td>
                                        <td className={''}>{new Date(k.created).toLocaleString()}</td>
                                        <td className={' flex items-center'}>
                                            <div className='flex items-center justify-center rounded-[0.375rem] bg-[#D0D5DD] pr-2'>
                                                <Input
                                                    type={'text'}
                                                    value={!!k.key ? k.key : `${k.prefix}.***`}
                                                    disabled
                                                    className='bg-[#D0D5DD]'
                                                />
                                                {!!k.key && (
                                                    <span
                                                        className={'cursor-pointer'}
                                                        onClick={() => {
                                                            navigator.clipboard
                                                                .writeText(k.key!)
                                                                .then(() => {
                                                                    toast('Copied', {type: 'success'});
                                                                })
                                                                .catch(() => {
                                                                    toast(
                                                                        'Failed to copy. Please copy the entire key from the text field above.',
                                                                        {type: 'error'},
                                                                    );
                                                                });
                                                        }}>
                                                        <div className='hidden md:flex'>
                                                            <CopyIcon width={25} height={25} color={'#667085'} />
                                                        </div>
                                                        <div className='flex md:hidden'>
                                                            <CopyIcon width={20} height={20} color={'#667085'} />
                                                        </div>
                                                    </span>
                                                )}
                                            </div>

                                            <div className='flex cursor-pointer pl-3' onClick={() => onDelete(k)}>
                                                <DeleteRed />
                                            </div>
                                        </td>
                                    </tr>
                                    {!!k.key && (
                                        <tr key={k.prefix + '_key_message'}>
                                            <td colSpan={4} className={'text-left'}>
                                                <span className={'italic text-gray-900'}>
                                                    Copy the key. This is the only time you will see it.
                                                </span>
                                            </td>
                                        </tr>
                                    )}
                                </>
                            );
                        })}
                        {keys.length === 0 && (
                            <tr className={''}>
                                <td colSpan={4} className={'text-left'}>
                                    <span className={'italic text-gray-900'}>
                                        No keys found. Create a new one above.
                                    </span>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function BaseFetch(props: {user: UserProfileInterface}) {
    const {user} = props;
    const org = user.currentOrganization;

    return (
        <DataLoader
            loader={async () => {
                const result = await ApiKeyCrud.list(org.uid);
                if (!!result.error) throw result.error;
                return result.data ?? [];
            }}>
            {(data) => <ApiKeyMgmt org_uid={org.uid} api_keys={data} />}
        </DataLoader>
    );
}

export function ApiKeyScreen() {
    const {API_KEY} = ScreenNames;

    return (
        <BaseScreen screenName={API_KEY}>
            <ScreenHeader icon={<KeyIcon className={'stroke-black'} />} title={'API Keys'} />
            <ScreenDivider />
            <UserLoader>{(user) => <BaseFetch user={user} />}</UserLoader>
            <ToastContainer />
        </BaseScreen>
    );
}
